import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Trophy, 
  TrendingUp, 
  Target, 
  BookOpen, 
  CheckCircle, 
  XCircle,
  Calendar,
  Award,
  Loader2
} from "lucide-react";
import { useQuizProgress } from "@/hooks/use-quiz";

const getDomainColor = (domain: string) => {
  switch (domain) {
    case "Strategic Thinking":
      return "bg-primary/10 text-primary border-primary/25";
    case "Executing":
      return "bg-secondary/10 text-brand-green-dark border-secondary/25";
    case "Influencing":
      return "bg-warning/10 text-warning border-warning/25";
    case "Relationship Building":
      return "bg-brand-teal-dark/10 text-brand-teal-dark border-brand-teal-dark/25";
    default:
      return "bg-secondary/20 text-secondary border-secondary/30";
  }
};

export const QuizProgressDashboard = () => {
  const { t } = useTranslation();
  const { data: progressData, isLoading, error } = useQuizProgress();

  if (isLoading) {
    return (
      <Card className="p-6 text-center sm:p-12">
        <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-muted-foreground">{t('quizProgress.loading')}</p>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6 text-center sm:p-12">
        <XCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
        <p className="text-muted-foreground">{t('quizProgress.error')}</p>
      </Card>
    );
  }

  if (!progressData || progressData.summary.totalContentAttempted === 0) {
    return (
      <Card className="p-6 text-center sm:p-12">
        <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
        <h3 className="text-xl font-semibold mb-2">{t('quizProgress.noProgress')}</h3>
        <p className="text-muted-foreground">
          {t('quizProgress.startTaking')}
        </p>
      </Card>
    );
  }

  const { summary, contentProgress } = progressData;
  const passedContent = contentProgress.filter(c => c.passed);
  const failedContent = contentProgress.filter(c => !c.passed);

  return (
    <div className="space-y-6">
      {/* Summary Statistics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="p-4 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20 sm:p-6">
          <div className="flex items-center gap-3 mb-2">
            <Target className="w-6 h-6 text-primary" />
            <h3 className="text-sm font-medium text-muted-foreground">
              {t('quizProgress.contentAttempted')}
            </h3>
          </div>
          <p className="text-3xl font-bold text-primary mb-1 sm:text-4xl">
            {summary.totalContentAttempted}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('quizProgress.totalAttempts', { count: summary.totalAttempts })}
          </p>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20 sm:p-6">
          <div className="flex items-center gap-3 mb-2">
            <Trophy className="w-6 h-6 text-accent" />
            <h3 className="text-sm font-medium text-muted-foreground">
              {t('quizProgress.averageScore')}
            </h3>
          </div>
          <p className="text-3xl font-bold text-accent mb-1 sm:text-4xl">
            {Math.round(summary.averageScore)}%
          </p>
          <Progress value={summary.averageScore} className="h-2" />
        </Card>

        <Card className="p-4 bg-gradient-to-br from-success/10 to-success/5 border-success/20 sm:p-6">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-6 h-6 text-success" />
            <h3 className="text-sm font-medium text-muted-foreground">
              {t('quizProgress.passRate')}
            </h3>
          </div>
          <p className="text-3xl font-bold text-success mb-1 sm:text-4xl">
            {Math.round(summary.passRate)}%
          </p>
          <p className="text-sm text-muted-foreground">
            {t('quizProgress.passed', { count: summary.totalContentPassed })}
          </p>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-warning/10 to-warning/5 border-warning/20 sm:p-6">
          <div className="flex items-center gap-3 mb-2">
            <Award className="w-6 h-6 text-warning" />
            <h3 className="text-sm font-medium text-muted-foreground">
              {t('quizProgress.successRate')}
            </h3>
          </div>
          <p className="text-3xl font-bold text-warning mb-1 sm:text-4xl">
            {Math.round((summary.totalContentPassed / summary.totalContentAttempted) * 100)}%
          </p>
          <Progress 
            value={(summary.totalContentPassed / summary.totalContentAttempted) * 100} 
            className="h-2" 
          />
        </Card>
      </div>

      {/* Content Progress Details */}
      <Tabs defaultValue="all" className="space-y-4">
        <div className="-mx-4 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
        <TabsList className="grid w-max min-w-full grid-cols-3">
          <TabsTrigger value="all" className="gap-2">
            <BookOpen className="w-4 h-4" />
            {t('quizProgress.all')} ({contentProgress.length})
          </TabsTrigger>
          <TabsTrigger value="passed" className="gap-2">
            <CheckCircle className="w-4 h-4" />
            {t('quizProgress.passedTab')} ({passedContent.length})
          </TabsTrigger>
          <TabsTrigger value="failed" className="gap-2">
            <XCircle className="w-4 h-4" />
            {t('quizProgress.inProgress')} ({failedContent.length})
          </TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="all" className="space-y-3">
          {contentProgress
            .sort((a, b) => new Date(b.lastAttemptDate).getTime() - new Date(a.lastAttemptDate).getTime())
            .map((content) => (
              <ContentProgressCard key={content.contentId} content={content} />
            ))}
        </TabsContent>

        <TabsContent value="passed" className="space-y-3">
          {passedContent.length > 0 ? (
            passedContent
              .sort((a, b) => b.bestScore - a.bestScore)
              .map((content) => (
                <ContentProgressCard key={content.contentId} content={content} />
              ))
          ) : (
            <Card className="p-6 text-center sm:p-12">
              <p className="text-muted-foreground">{t('quizProgress.noPassedYet')}</p>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="failed" className="space-y-3">
          {failedContent.length > 0 ? (
            failedContent
              .sort((a, b) => b.bestScore - a.bestScore)
              .map((content) => (
                <ContentProgressCard key={content.contentId} content={content} />
              ))
          ) : (
            <Card className="p-6 text-center sm:p-12">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-accent" />
              <p className="text-muted-foreground">{t('quizProgress.allPassed')}</p>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

interface ContentProgressCardProps {
  content: {
    contentId: string;
    strengthName: string;
    domain: string;
    shortDescription: string;
    totalAttempts: number;
    bestScore: number;
    passed: boolean;
    lastAttemptDate: string;
  };
}

const ContentProgressCard = ({ content }: ContentProgressCardProps) => {
  const { t } = useTranslation();
  
  return (
    <Card className="p-4 hover:bg-muted/50 transition-colors sm:p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <h3 className="break-words text-lg font-semibold">{content.strengthName}</h3>
            <Badge className={getDomainColor(content.domain)}>
              {content.domain}
            </Badge>
          </div>
          
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
            {content.shortDescription}
          </p>

          <div className="flex flex-wrap items-center gap-3 text-sm sm:gap-4">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>{new Date(content.lastAttemptDate).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <Trophy className="w-4 h-4" />
              <span>{t('quizProgress.attempts', { count: content.totalAttempts })}</span>
            </div>
          </div>
        </div>

        <div className="text-start sm:text-right">
          <div className="mb-2">
            <p className="text-3xl font-bold text-primary">
              {content.bestScore}%
            </p>
            <p className="text-xs text-muted-foreground">{t('quizProgress.bestScore')}</p>
          </div>
          {content.passed ? (
            <Badge className="status-success">
              <CheckCircle className="w-3 h-3 mr-1" />
              {t('quizProgress.passedBadge')}
            </Badge>
          ) : (
            <Badge className="status-warning">
              <XCircle className="w-3 h-3 mr-1" />
              {t('quizProgress.inProgressBadge')}
            </Badge>
          )}
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span>{t('quizProgress.progressToPass')}</span>
          <span>{Math.min(content.bestScore, 100)}/70</span>
        </div>
        <Progress 
          value={Math.min((content.bestScore / 70) * 100, 100)} 
          className="h-2" 
        />
      </div>
    </Card>
  );
};
